import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { fetchJSON } from '../utils/api';
import { fmtPercent, QuizVerdictBadge } from '../components/lnd/quizFormat';
import {
  SecurePaper, useKeyGuard, useFocusLoss, pickAnswer, isAnswered,
  QuestionStep, ReviewStep, ConfirmSubmitModal, SittingRules, QuizFacts, QuizResultCard,
} from '../components/lnd/QuizSitting';

/**
 * Post-training quiz — /quiz/:sessionId (docs/modules/ld-selfservice.md §3.1).
 * Reached from the quiz-request email or the Overview follow-up. Login only;
 * the server decides who may sit it (the session's quiz audience).
 *
 * Three screens: intro (rules, Start / Resume, or the result once done) →
 * the sitting, one question at a time with Previous / Next and a review
 * step → the result. Sit until passed: a result of Not passed offers Retake,
 * which opens the next attempt (fresh shuffle); every attempt is kept and
 * the intro lists them. The answer key is never shown, whichever attempt.
 * The screens themselves live in components/lnd/QuizSitting.js, shared with
 * the Preview.
 *
 * What keeps the sitting honest (best effort — a determined person can always
 * photograph a screen, so HR also sees the time taken and how often the tab
 * lost focus):
 *   - the paper only arrives after Start, in this person's own shuffled
 *     order (questions AND options), without the correct keys
 *   - one open sitting at a time: the clock starts on the server at Start
 *     and runs until Submit; a refresh resumes with the same clock and answers.
 *     A sitting left idle (no answer, no heartbeat) for the server's idle
 *     limit expires — it counts as an attempt and the next one starts over.
 *     The elapsed time is deliberately NEVER shown here — not while
 *     answering, not on the result screen. It lives in ld_quiz_attempts and
 *     surfaces only on the HR side (session Quiz tab / Quiz Results); a
 *     visible countdown would tell a colleague exactly how long "normal"
 *     looks, and the pressure of a ticking clock helps nobody answer better
 *   - selecting, copying, cutting, pasting, dragging and the context menu are
 *     disabled on the paper; Ctrl/⌘ shortcuts for copy/print/save are blocked
 *     and the page prints blank
 *   - every time the tab is hidden, the window loses focus or the person
 *     leaves the page through an in-app link it is counted and reported to
 *     the server. The count is NOT shown here — only HR sees it, next to the
 *     score (the Preview shows it, so HR can see what gets counted)
 *   - the reader's email is watermarked across the paper
 */

const AUTOSAVE_MS = 800;
// While the paper is on screen and focused, tell the server the sitter is
// still here, well inside the idle limit.
const HEARTBEAT_MS = 60 * 1000;

// Every closed sitting so far, oldest first — the intro shows it so a person
// coming back knows where they stand. Scores and verdicts only.
const AttemptHistory = ({ attempts }) => {
  const closed = (attempts || []).filter((a) => a.submittedAt || a.expiredAt);
  if (closed.length === 0) return null;
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <p className="text-sm font-semibold text-slate-900 mb-2">Your Attempts</p>
      <ul className="text-sm text-slate-700 space-y-1">
        {closed.map((a) => (
          <li key={a.attemptNo} className="flex items-center justify-between gap-3">
            <span>Attempt {a.attemptNo} · {formatDateTime(a.submittedAt || a.expiredAt)}</span>
            {a.submittedAt ? (
              <span className="inline-flex items-center gap-2 tabular-nums">
                {a.score} / {a.maxScore} ({fmtPercent(a.percent)})
                {a.passed == null ? <span className="text-xs text-slate-500">Score only</span> : <QuizVerdictBadge passed={a.passed} />}
              </span>
            ) : (
              <span className="text-xs text-slate-500">Expired — not graded</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const QuizPage = () => {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);          // GET /quiz-form
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState('intro');     // intro | sitting | review | done
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [attemptNo, setAttemptNo] = useState(1);   // the open sitting's number
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  const saveTimer = useRef(null);
  const answersRef = useRef({});
  answersRef.current = answers;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const load = useCallback(async () => {
    try {
      const d = await fetchJSON(`/api/ld/quiz-form/${sessionId}`);
      setData(d);
      // Done = land on the result. Not passed = the intro, where the
      // history and a Retake button wait (same place as the first Start).
      if (d.done && d.attempt?.status === 'submitted') {
        setResult(d.attempt);
        setPhase('done');
      } else {
        setPhase('intro');
      }
      setError(null);
    } catch (e) { setError(e.message); }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  const sitting = phase === 'sitting' || phase === 'review';

  // The sitting expired while away: back to the intro, which offers the
  // next attempt, with the server's explanation on top.
  const onSyncError = useCallback((e) => {
    if (e.code !== 'QUIZ_EXPIRED') return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setPhase('intro');
    load().then(() => setError(e.message));
  }, [load]);

  // Every write to the open sitting (autosave, focus report, heartbeat) also
  // restarts its idle clock on the server.
  const report = useCallback((body) => fetchJSON(`/api/ld/quiz-form/${sessionId}/progress`, {
    method: 'PATCH', body: JSON.stringify(body),
  }).catch(onSyncError), [sessionId, onSyncError]);

  // Focus loss is counted on the server only; the participant sees nothing.
  // Coming back pings at once, which catches a sitting that expired meanwhile.
  const awayRef = useFocusLoss(sitting, {
    onAway: () => report({ violations: 1 }),
    onBack: () => report({}),
  });
  useKeyGuard(sitting, { confirmLeave: true });

  // Heartbeat, only while the sitter is present.
  useEffect(() => {
    if (!sitting) return undefined;
    const beat = setInterval(() => {
      if (!document.hidden && document.hasFocus()) report({});
    }, HEARTBEAT_MS);
    return () => clearInterval(beat);
  }, [sitting, report]);

  // Autosave answers (debounced) so a refresh resumes where it left off.
  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => report({ answers: answersRef.current }), AUTOSAVE_MS);
  }, [report]);

  // Leaving the page through an in-app link fires neither blur nor
  // visibilitychange: count it like leaving the tab, and save the answers
  // the debounce had not sent yet.
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (phaseRef.current !== 'sitting' && phaseRef.current !== 'review') return;
    fetchJSON(`/api/ld/quiz-form/${sessionId}/progress`, {
      method: 'PATCH',
      body: JSON.stringify({ answers: answersRef.current, violations: awayRef.current ? 0 : 1 }),
    }).catch(() => {});
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sitting lifecycle ──────────────────────────────────────────────────────
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = await fetchJSON(`/api/ld/quiz-form/${sessionId}/start`, { method: 'POST' });
      setQuestions(d.questions || []);
      setAnswers(d.answers || {});
      setAttemptNo(d.attempt?.attemptNo || 1);
      setIndex(0);
      awayRef.current = false;
      setPhase('sitting');
    } catch (e) {
      setError(e.message);
      if (e.code === 'QUIZ_SUBMITTED') load();
    } finally { setBusy(false); }
  };

  // Not passed → open the next attempt. Same call as Start: the server hands
  // out attempt N+1 with a fresh shuffle.
  const retake = async () => {
    setResult(null);
    await start();
  };

  // ── Answering ──────────────────────────────────────────────────────────────
  const pick = (q, key) => {
    setAnswers((prev) => pickAnswer(prev, q, key));
    scheduleSave();
  };
  const unanswered = useMemo(() => questions.filter((q) => !isAnswered(answers, q)).length, [questions, answers]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    try {
      const d = await fetchJSON(`/api/ld/quiz-form/${sessionId}/submit`, {
        method: 'POST', body: JSON.stringify({ answers: answersRef.current }),
      });
      setResult(d.result);
      setConfirmSubmit(false);
      setPhase('done');
    } catch (e) {
      setError(e.message);
      setConfirmSubmit(false);
      if (e.code === 'QUIZ_SUBMITTED') load();
      if (e.code === 'QUIZ_EXPIRED') onSyncError(e);
    } finally { setBusy(false); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const email = user?.email || '';

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 relative">
        <div className="mb-4">
          <h1 className="text-lg font-bold text-slate-900">Post-Training Quiz</h1>
          {data?.quiz && sitting && (
            <p className="text-xs text-slate-500 truncate">{data.quiz.title}{attemptNo > 1 ? ` · Attempt ${attemptNo}` : ''}</p>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Intro */}
        {phase === 'intro' && data && (
          <div className="space-y-5">
            <QuizFacts session={data.session} quiz={data.quiz} />
            <AttemptHistory attempts={data.attempts} />
            <SittingRules
              passMark={data.quiz.passMark}
              mustPassCount={data.quiz.mustPassCount}
              idleExpiryMinutes={data.idleExpiryMinutes}
            />
            {!data.open ? (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
                This quiz opens {data.opensAt && data.opensAt.slice(11) !== '00:00' ? `at ${data.opensAt.slice(11)} ` : ''}on {formatDate(data.session.date)}, once the session has ended.
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-slate-500">
                  {data.attempt?.status === 'in_progress'
                    ? `You started ${data.attempt.attemptNo > 1 ? `attempt ${data.attempt.attemptNo}` : 'this quiz'} ${formatDateTime(data.attempt.startedAt)} — your answers so far are saved.`
                    : data.attempt?.status === 'expired'
                      ? `Attempt ${data.attempt.attemptNo} expired after ${data.idleExpiryMinutes} minutes away — attempt ${data.attempt.attemptNo + 1} starts when you are ready.`
                      : data.canRetake
                        ? `Not passed so far — attempt ${(data.attempt?.attemptNo || 0) + 1} starts when you are ready.`
                        : 'Ready when you are.'}
                </p>
                <button
                  type="button"
                  onClick={start}
                  disabled={busy}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {busy ? 'Opening…' : (data.attempt?.status === 'in_progress' ? 'Resume Quiz' : (data.canRetake ? 'Retake Quiz' : 'Start Quiz'))}
                </button>
              </div>
            )}
          </div>
        )}

        {/* The sitting */}
        {phase === 'sitting' && questions[index] && (
          <SecurePaper watermark={email}>
            <QuestionStep
              questions={questions}
              index={index}
              answers={answers}
              onPick={pick}
              onIndex={setIndex}
              onReview={() => setPhase('review')}
            />
          </SecurePaper>
        )}

        {/* Review: which questions are answered, then submit */}
        {phase === 'review' && (
          <SecurePaper watermark={email}>
            <ReviewStep
              questions={questions}
              answers={answers}
              onOpen={(i) => { setIndex(i); setPhase('sitting'); }}
              onSubmit={() => setConfirmSubmit(true)}
              busy={busy}
            />
          </SecurePaper>
        )}

        {/* Result */}
        {phase === 'done' && result && data && (
          <div className="space-y-4">
            <QuizFacts session={data.session} quiz={data.quiz} />
            <QuizResultCard result={result} onRetake={result.passed === false ? retake : null} busy={busy} />
          </div>
        )}

        {!data && !error && <p className="text-sm text-slate-500">Loading…</p>}

        <div className="mt-6 border-t border-slate-100 pt-4">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
        </div>
      </div>

      {confirmSubmit && (
        <ConfirmSubmitModal
          unanswered={unanswered}
          busy={busy}
          onCancel={() => setConfirmSubmit(false)}
          onConfirm={submit}
        />
      )}
    </div>
  );
};

export default QuizPage;
